// aiUsage / route — the AI-usage admin pages' URL, parsed and serialized.
//
// The URL is the whole view state (tab, period, filters, sort, page, the
// open call), so a view can be linked and survives a reload. Pure: the host
// reads `location` and pushes history; this module only translates.
//
//   /ops/ai-usage            overview
//   /ops/ai-usage/calls      the call list (?call=<id> opens its detail)
//   /ops/ai-usage/rates      the rate table

export type { AiUsageFilterKey, AiUsageRoute, AiUsageTab, PeriodPreset };
export {
    AI_USAGE_PATH,
    FILTER_KEYS,
    isAiUsagePath,
    parseAiUsageRoute,
    serializeAiUsageRoute,
    withFilter,
    withoutFilter,
};

import {
    type AiUsageBreakdownBy,
    type AiUsageCostFilter,
    type AiUsageOutcomeFilter,
    type AiUsageSort,
    aiUsageBreakdownBy,
    aiUsageCostFilters,
    aiUsageOutcomeFilters,
    aiUsageSiteTypes,
    aiUsageSorts,
} from "@bilda/app/agentCallAdmin";

const AI_USAGE_PATH = "/ops/ai-usage";

type AiUsageTab = "overview" | "calls" | "rates";
type PeriodPreset = "day" | "week" | "month";

type AiUsageFilters = {
    readonly user?: string;
    readonly workspace?: string;
    readonly callType?: (typeof aiUsageSiteTypes)[number];
    readonly model?: string;
    readonly outcome?: AiUsageOutcomeFilter;
    readonly cost?: AiUsageCostFilter;
    readonly entity?: string;
    readonly file?: string;
    readonly tool?: string;
    readonly q?: string;
};
type AiUsageFilterKey = keyof AiUsageFilters;

const FILTER_KEYS: readonly AiUsageFilterKey[] = [
    "user",
    "workspace",
    "callType",
    "model",
    "outcome",
    "cost",
    "entity",
    "file",
    "tool",
    "q",
];

type AiUsageRoute = {
    readonly tab: AiUsageTab;
    readonly period: PeriodPreset;
    readonly filters: AiUsageFilters;
    readonly sort: AiUsageSort;
    readonly dir: "asc" | "desc";
    // 0-based page of the call list.
    readonly page: number;
    readonly by: AiUsageBreakdownBy;
    readonly callId?: string;
};

const isAiUsagePath = (pathname: string): boolean =>
    pathname === AI_USAGE_PATH || pathname.startsWith(`${AI_USAGE_PATH}/`);

const oneOf = <T extends string>(
    allowed: readonly T[],
    v: string | null,
): T | undefined => allowed.find((a) => a === v);

const text = (v: string | null): string | undefined =>
    v === null || v.trim() === "" ? undefined : v.trim();

const tabOf = (pathname: string): AiUsageTab | undefined => {
    const rest = pathname.slice(AI_USAGE_PATH.length).replace(/\/+$/, "");
    if (rest === "") return "overview";
    if (rest === "/calls") return "calls";
    if (rest === "/rates") return "rates";
    return undefined;
};

// Undefined when the path is not one of these pages. Unknown query values
// fall back to the defaults, so a stale link still opens.
const parseAiUsageRoute = (
    pathname: string,
    search: string,
): AiUsageRoute | undefined => {
    if (!isAiUsagePath(pathname)) return undefined;
    const tab = tabOf(pathname);
    if (tab === undefined) return undefined;
    const p = new URLSearchParams(search);
    const user = text(p.get("user"));
    const workspace = text(p.get("workspace"));
    const callType = oneOf(aiUsageSiteTypes, p.get("callType"));
    const model = text(p.get("model"));
    const outcome = oneOf(aiUsageOutcomeFilters, p.get("outcome"));
    const cost = oneOf(aiUsageCostFilters, p.get("cost"));
    const entity = text(p.get("entity"));
    const file = text(p.get("file"));
    const tool = text(p.get("tool"));
    const q = text(p.get("q"));
    const filters: AiUsageFilters = {
        ...(user === undefined ? {} : { user }),
        ...(workspace === undefined ? {} : { workspace }),
        ...(callType === undefined ? {} : { callType }),
        ...(model === undefined ? {} : { model }),
        ...(outcome === undefined ? {} : { outcome }),
        ...(cost === undefined ? {} : { cost }),
        ...(entity === undefined ? {} : { entity }),
        ...(file === undefined ? {} : { file }),
        ...(tool === undefined ? {} : { tool }),
        ...(q === undefined ? {} : { q }),
    };
    const page = Number.parseInt(p.get("page") ?? "", 10);
    const callId = text(p.get("call"));
    return {
        tab,
        period: oneOf(["day", "week", "month"], p.get("period")) ?? "month",
        filters,
        sort: oneOf(aiUsageSorts, p.get("sort")) ?? "time",
        dir: p.get("dir") === "asc" ? "asc" : "desc",
        page: Number.isFinite(page) && page > 0 ? page : 0,
        by: oneOf(aiUsageBreakdownBy, p.get("by")) ?? "workspace",
        ...(callId === undefined ? {} : { callId }),
    };
};

const pathOf = (tab: AiUsageTab): string =>
    tab === "overview" ? AI_USAGE_PATH : `${AI_USAGE_PATH}/${tab}`;

// Canonical URL: defaults are left out so links stay short.
const serializeAiUsageRoute = (r: AiUsageRoute): string => {
    const p = new URLSearchParams();
    if (r.period !== "month") p.set("period", r.period);
    for (const key of FILTER_KEYS) {
        const v = r.filters[key];
        if (v !== undefined) p.set(key, v);
    }
    if (r.tab === "calls") {
        if (r.sort !== "time") p.set("sort", r.sort);
        if (r.dir !== "desc") p.set("dir", r.dir);
        if (r.page > 0) p.set("page", String(r.page));
        if (r.callId !== undefined) p.set("call", r.callId);
    }
    if (r.tab === "overview" && r.by !== "workspace") p.set("by", r.by);
    const qs = p.toString();
    return qs === "" ? pathOf(r.tab) : `${pathOf(r.tab)}?${qs}`;
};

// A filter changed: back to the first page, no call open.
const withFilter = <K extends AiUsageFilterKey>(
    r: AiUsageRoute,
    key: K,
    value: AiUsageFilters[K],
): AiUsageRoute => {
    const { callId: _closed, ...rest } = r;
    return { ...rest, page: 0, filters: { ...r.filters, [key]: value } };
};

const withoutFilter = (
    r: AiUsageRoute,
    key: AiUsageFilterKey,
): AiUsageRoute => {
    const { [key]: _dropped, ...filters } = r.filters;
    const { callId: _closed, ...rest } = r;
    return { ...rest, page: 0, filters };
};
